/**
 * Enquiry context hydration — MH's server-first equivalent of Simonds'
 * per-page setDynamicHiddenFields() store (simonds-fe/app/stores/forms.js +
 * components/form-builder.vue:38-45,277). Each page server-renders
 * #enquiry-context describing what the visitor is looking at; on load and after
 * every Swup navigation we copy those values into the matching hidden inputs of
 * EVERY enquiry form on the page — the embedded detail form AND the persistent
 * global contact modal (which lives outside #swup, so it must be re-hydrated
 * per page or it would carry the previous page's context).
 *
 * The known context fields are cleared first, then set from the current page's
 * block, so the modal never leaks stale context across a navigation. Empty
 * values are left empty; the submission listener strips empties server-side too.
 *
 * conversion_page is handled separately — see setConversionPage below. It isn't
 * page CONTENT, it's the page itself, so it comes from window.location rather
 * than the context block.
 */
const CONTEXT_FIELDS = [
    'content_type',
    'content_name',
    'home_design',
    'estate_name',
    'listing_agent_email',
    'listing_agent_name',
    'suburb',
    'bedrooms',
    'bathrooms',
    'internal_area_sqm',
];

/**
 * Fill a form's conversion_page — "which page did this lead come from" — from
 * the current URL.
 *
 * Only the embedded detail form and the home-finder wizard render a value
 * server-side; both mark it `data-server-set` and are skipped here, so a
 * deliberate value (the wizard pins /home-finder) and the no-JS path both
 * survive. The global contact modal passes nothing, so before this its
 * conversion_page submitted empty on every page — HubSpot got no pageUri and
 * GA4's form_submit dropped the dimension.
 *
 * The modal is mounted once OUTSIDE #swup, so it cannot be fixed server-side:
 * any rendered URL goes stale on the first Swup navigation. Assigning
 * unconditionally on every hydration (rather than only when empty) is what
 * keeps it correct across navigations.
 *
 * Mirrors Statamic's `current_url` — $request->url(), i.e. origin + path with
 * no query string — so modal leads and detail-form leads are directly
 * comparable in reporting.
 *
 * @param {HTMLFormElement} form
 */
function setConversionPage(form) {
    const input = form.querySelector('input[type="hidden"][name="conversion_page"]');

    if (!input || input.hasAttribute('data-server-set')) {
        return;
    }

    input.value = window.location.origin + window.location.pathname;
}

export function hydrateEnquiryContext() {
    // The modal survives Swup navigation; replace its brochure even when the
    // destination has none, so a previous design’s download cannot linger.
    const brochure = document.querySelector('#swup template[data-enquiry-brochure]');
    document.querySelectorAll('[data-enquiry-brochure-slot]').forEach((slot) => {
        slot.replaceChildren(...(brochure ? [brochure.content.cloneNode(true)] : []));
        slot.hidden = !brochure;
        const difference = slot.closest('.mh-form-modal__sidebar')?.querySelector('.mh-form-modal__aside');
        if (difference) {
            difference.hidden = Boolean(brochure);
        }
        slot.closest('.mh-form-modal__form')?.classList.toggle('mh-form-modal__form--has-attachment', Boolean(brochure));
    });

    const context = {};
    // A page may render a generic fallback block (in the layout) AND a specific
    // one (in a detail template); the most specific wins, so take the LAST.
    const blocks = document.querySelectorAll('[data-enquiry-context]');
    const block = blocks.length ? blocks[blocks.length - 1] : null;

    if (block) {
        block.querySelectorAll('[data-field]').forEach((el) => {
            const field = el.getAttribute('data-field');
            const value = el.getAttribute('data-value');
            if (field && value) {
                context[field] = value;
            }
        });
    }

    document.querySelectorAll('form.mh-form').forEach((form) => {
        CONTEXT_FIELDS.forEach((field) => {
            const input = form.querySelector(`input[type="hidden"][name="${field}"]`);
            if (input) {
                input.value = context[field] || '';
            }
        });

        setConversionPage(form);
    });
}
